import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import '../../styles/profile-cropper-mobile.css';

const ProfilePhotoCropper = ({ imageFile, onCropComplete, onCancel, isModal = false, showCancelButton = true }) => {
  const { isDarkMode } = useTheme();
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropSize, setCropSize] = useState(200);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 400 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [minCropSize, setMinCropSize] = useState(80);
  const [maxCropSize, setMaxCropSize] = useState(300);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Load image when component mounts or imageFile changes
  useEffect(() => {
    if (imageFile) {
      // Reset states
      setError(null);
      setImageLoaded(false);
      setImage(null);
      
      // Validate file object
      if (!imageFile || !(imageFile instanceof File)) {
        console.error('Invalid file object:', imageFile);
        setError('Invalid file. Please try uploading again.');
        return;
      }
      
      // Validate file type
      if (!imageFile.type.startsWith('image/')) {
        console.error('Invalid file type:', imageFile.type);
        setError('Please select a valid image file.');
        return;
      }
      
      // Validate file size
      if (imageFile.size === 0) {
        console.error('Empty file:', imageFile.size);
        setError('The selected file appears to be empty. Please try a different image.');
        return;
      }
      
      // Debug logging (can be removed in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('Loading image file:', {
          name: imageFile.name,
          type: imageFile.type,
          size: imageFile.size
        });
      }
      
      let objectUrl;
      
      try {
        objectUrl = URL.createObjectURL(imageFile);
        
        const img = new Image();
        
        img.onload = () => {
          try {
            setImage(img);
            
            // Calculate canvas size and initial crop position
            // Make it responsive for mobile
            const isMobile = window.innerWidth < 768;
            const maxSize = isModal ? (isMobile ? 320 : 400) : (isMobile ? 300 : 350);
            const aspectRatio = img.width / img.height;
            let canvasWidth, canvasHeight;
            
            if (aspectRatio > 1) {
              canvasWidth = Math.min(maxSize, img.width);
              canvasHeight = canvasWidth / aspectRatio;
            } else {
              canvasHeight = Math.min(maxSize, img.height);
              canvasWidth = canvasHeight * aspectRatio;
            }
            
            setCanvasSize({ width: canvasWidth, height: canvasHeight });
            
            // Set crop size constraints based on canvas size
            const minDimension = Math.min(canvasWidth, canvasHeight);
            const newMinCropSize = Math.max(60, minDimension * 0.2);
            const newMaxCropSize = Math.min(minDimension * 0.9, 400);
            const initialCropSize = Math.min(minDimension * 0.6, 200);
            
            setMinCropSize(newMinCropSize);
            setMaxCropSize(newMaxCropSize);
            setCropSize(initialCropSize);
            
            // Center the crop initially
            setCropPosition({
              x: canvasWidth / 2,
              y: canvasHeight / 2
            });
            
            setImageLoaded(true);
          } catch (error) {
            console.error('Error processing loaded image:', error);
            setError('Failed to process the image. Please try a different photo.');
          }
        };
        
        img.onerror = (error) => {
          console.error('Failed to load image for cropping:', error);
          
          // Try alternative loading method with FileReader
          const reader = new FileReader();
          reader.onload = (e) => {
            const fallbackImg = new Image();
            fallbackImg.onload = () => {
              try {
                setImage(fallbackImg);
                
                // Calculate canvas size and initial crop position
                const isMobile = window.innerWidth < 768;
                const maxSize = isModal ? (isMobile ? 320 : 400) : (isMobile ? 300 : 350);
                const aspectRatio = fallbackImg.width / fallbackImg.height;
                let canvasWidth, canvasHeight;
                
                if (aspectRatio > 1) {
                  canvasWidth = Math.min(maxSize, fallbackImg.width);
                  canvasHeight = canvasWidth / aspectRatio;
                } else {
                  canvasHeight = Math.min(maxSize, fallbackImg.height);
                  canvasWidth = canvasHeight * aspectRatio;
                }
                
                setCanvasSize({ width: canvasWidth, height: canvasHeight });
                
                const minDimension = Math.min(canvasWidth, canvasHeight);
                const newMinCropSize = Math.max(60, minDimension * 0.2);
                const newMaxCropSize = Math.min(minDimension * 0.9, 400);
                const initialCropSize = Math.min(minDimension * 0.6, 200);
                
                setMinCropSize(newMinCropSize);
                setMaxCropSize(newMaxCropSize);
                setCropSize(initialCropSize);
                
                setCropPosition({
                  x: canvasWidth / 2,
                  y: canvasHeight / 2
                });
                
                setImageLoaded(true);
                if (process.env.NODE_ENV === 'development') {
                  console.log('Image loaded successfully using FileReader fallback');
                }
              } catch (fallbackError) {
                console.error('Fallback image loading also failed:', fallbackError);
                setError('Failed to load image. Please try uploading a different photo.');
              }
            };
            
            fallbackImg.onerror = () => {
              console.error('Fallback image loading failed');
              setError('Failed to load image. Please try uploading a different photo.');
            };
            
            fallbackImg.src = e.target.result;
          };
          
          reader.onerror = () => {
            console.error('FileReader failed');
            setError('Failed to load image. Please try uploading a different photo.');
          };
          
          reader.readAsDataURL(imageFile);
        };
        
        // Set crossOrigin to handle potential CORS issues
        img.crossOrigin = 'anonymous';
        
        // Add timeout for loading
        const loadTimeout = setTimeout(() => {
          setError('Image loading timed out. Please try a different photo.');
        }, 10000); // 10 second timeout
        
        const originalOnLoad = img.onload;
        img.onload = (e) => {
          clearTimeout(loadTimeout);
          originalOnLoad(e);
        };
        
        const originalOnError = img.onerror;
        img.onerror = (e) => {
          clearTimeout(loadTimeout);
          if (originalOnError) originalOnError(e);
        };
        
        img.src = objectUrl;
        
      } catch (error) {
        console.error('Error creating object URL:', error);
        setError('Failed to process the uploaded file. Please try again.');
      }
      
      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      };
    }
  }, [imageFile, isModal]);

  // Draw the image and crop overlay on canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image || !imageLoaded) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvasSize.width, canvasSize.height);
    
    // Draw image scaled to fit canvas
    ctx.drawImage(image, 0, 0, canvasSize.width, canvasSize.height);
    
    // Draw semi-transparent overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
    
    // Clear the crop circle area
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(cropPosition.x, cropPosition.y, cropSize / 2, 0, 2 * Math.PI);
    ctx.fill();
    
    // Draw crop circle border
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = isDarkMode ? '#ffffff' : '#000000';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cropPosition.x, cropPosition.y, cropSize / 2, 0, 2 * Math.PI);
    ctx.stroke();
    
    // Draw center dot for better visibility
    ctx.fillStyle = isDarkMode ? '#ffffff' : '#000000';
    ctx.beginPath();
    ctx.arc(cropPosition.x, cropPosition.y, 3, 0, 2 * Math.PI);
    ctx.fill();
  }, [image, imageLoaded, canvasSize, cropPosition, cropSize, isDarkMode]);

  // Redraw canvas when dependencies change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Constrain crop position to stay within image bounds
  const constrainPosition = useCallback((x, y, size = cropSize) => {
    const radius = size / 2;
    const constrainedX = Math.max(radius, Math.min(canvasSize.width - radius, x));
    const constrainedY = Math.max(radius, Math.min(canvasSize.height - radius, y));
    return { x: constrainedX, y: constrainedY };
  }, [cropSize, canvasSize]);

  // Handle crop size change from slider
  const handleCropSizeChange = useCallback((newSize) => {
    setCropSize(newSize);
    // Ensure position is still valid with new size
    const constrainedPosition = constrainPosition(cropPosition.x, cropPosition.y, newSize);
    setCropPosition(constrainedPosition);
  }, [cropPosition, constrainPosition]);

  // Handle mouse/touch start
  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = ((clientX - rect.left) / rect.width) * canvasSize.width;
    const y = ((clientY - rect.top) / rect.height) * canvasSize.height;
    
    // Enhanced mobile touch handling
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      // On mobile, allow dragging from anywhere on the canvas
      setIsDragging(true);
      setDragStart({ x: x - cropPosition.x, y: y - cropPosition.y });
    } else {
      // On desktop, only allow dragging from within the crop circle
      const distance = Math.sqrt(
        Math.pow(x - cropPosition.x, 2) + Math.pow(y - cropPosition.y, 2)
      );
      
      if (distance <= cropSize / 2) {
        setIsDragging(true);
        setDragStart({ x: x - cropPosition.x, y: y - cropPosition.y });
      }
    }
  }, [canvasSize, cropPosition, cropSize]);

  // Handle mouse/touch move
  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = ((clientX - rect.left) / rect.width) * canvasSize.width;
    const y = ((clientY - rect.top) / rect.height) * canvasSize.height;
    
    const newPosition = constrainPosition(x - dragStart.x, y - dragStart.y);
    setCropPosition(newPosition);
  }, [isDragging, canvasSize, dragStart, constrainPosition]);

  // Handle mouse/touch end
  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setDragStart({ x: 0, y: 0 });
  }, []);

  // Add event listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Mouse events
    canvas.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    
    // Touch events
    canvas.addEventListener('touchstart', handlePointerDown, { passive: false });
    document.addEventListener('touchmove', handlePointerMove, { passive: false });
    document.addEventListener('touchend', handlePointerUp);
    
    return () => {
      canvas.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('mouseup', handlePointerUp);
      canvas.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('touchmove', handlePointerMove);
      document.removeEventListener('touchend', handlePointerUp);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  // Generate cropped image blob
  const handleConfirm = useCallback(async () => {
    if (!image || !imageLoaded) return;
    
    setIsProcessing(true);
    
    try {
      // Create a new canvas for the cropped result
      const cropCanvas = document.createElement('canvas');
      const cropCtx = cropCanvas.getContext('2d');
      
      // Set output size (standard profile picture size)
      const outputSize = 400;
      cropCanvas.width = outputSize;
      cropCanvas.height = outputSize;
      
      // Calculate source crop area in original image coordinates
      const scaleX = image.width / canvasSize.width;
      const scaleY = image.height / canvasSize.height;
      
      const sourceX = (cropPosition.x - cropSize / 2) * scaleX;
      const sourceY = (cropPosition.y - cropSize / 2) * scaleY;
      const sourceSize = cropSize * Math.min(scaleX, scaleY);
      
      // Draw cropped and scaled image
      cropCtx.drawImage(
        image,
        sourceX, sourceY, sourceSize, sourceSize,
        0, 0, outputSize, outputSize
      );
      
      // Convert to blob
      cropCanvas.toBlob((blob) => {
        if (blob) {
          onCropComplete(blob);
        } else {
          console.error('Failed to generate cropped image blob');
          setError('Failed to process the cropped image. Please try again.');
          setIsProcessing(false);
        }
      }, 'image/jpeg', 0.9);
      
    } catch (error) {
      console.error('Error cropping image:', error);
      setError('An error occurred while processing your image. Please try again.');
      setIsProcessing(false);
    }
  }, [image, imageLoaded, canvasSize, cropPosition, cropSize, onCropComplete, onCancel]);

  if (error) {
    return (
      <div className="flex flex-col items-center space-y-4 p-8">
        <div className={`text-center ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
          <p className="font-medium mb-2">Unable to Load Image</p>
          <p className="text-sm mb-3">{error}</p>
          <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} space-y-1`}>
            <p>💡 Try these solutions:</p>
            <p>• Make sure the file is a valid image (JPEG, PNG, GIF, WebP)</p>
            <p>• Check that the file isn't corrupted</p>
            <p>• Try a different image file</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            isDarkMode
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
          }`}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!imageLoaded) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        <span className={`ml-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Loading image...
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4 sm:space-y-6 pb-4 sm:pb-4">
      {/* Instructions */}
      <div className="text-center px-3 sm:px-4 max-w-md">
        <h3 className={`text-xl sm:text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} mb-3 sm:mb-2`}>
          Adjust Your Photo
        </h3>
        <p className={`text-base sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-3 sm:mb-2 leading-relaxed`}>
          {window.innerWidth < 768 
            ? 'Tap and drag to position your face' 
            : 'Drag the circle to position your face in the center'
          }
        </p>
        {/* Show simplified tips on mobile */}
        <div className={`text-sm sm:text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'} space-y-1`}>
          <p className="sm:hidden">💡 Use the slider to resize</p>
          <div className="hidden sm:block space-y-1">
            <p>💡 Position your face in the center of the circle</p>
            <p>🔄 Use the slider below to resize the crop area</p>
            <p>📱 The cropped area will be your profile picture</p>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative px-3 sm:px-4 w-full flex justify-center">
        <canvas
          ref={canvasRef}
          className={`border-2 rounded-lg cursor-move ${
            isDarkMode ? 'border-gray-600' : 'border-gray-300'
          } ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} max-w-full shadow-lg`}
          style={{
            width: Math.min(canvasSize.width, window.innerWidth - 24),
            height: 'auto',
            touchAction: 'none',
            maxWidth: '100%',
            // Give more space on mobile - 50vh instead of 40vh
            maxHeight: window.innerWidth < 768 ? '50vh' : 'auto'
          }}
        />
      </div>

      {/* Crop Size Slider */}
      <div className="px-3 sm:px-4 w-full max-w-sm mx-auto">
        <div className="flex items-center space-x-3 sm:space-x-3">
          <span className={`text-sm sm:text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Size:
          </span>
          <div className="flex-1 relative">
            <input
              type="range"
              min={minCropSize}
              max={maxCropSize}
              value={cropSize}
              onChange={(e) => handleCropSizeChange(parseInt(e.target.value))}
              className={`w-full h-3 sm:h-2 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              } mobile-slider`}
              style={{
                background: isDarkMode 
                  ? `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${((cropSize - minCropSize) / (maxCropSize - minCropSize)) * 100}%, #374151 ${((cropSize - minCropSize) / (maxCropSize - minCropSize)) * 100}%, #374151 100%)`
                  : `linear-gradient(to right, #8b5cf6 0%, #8b5cf6 ${((cropSize - minCropSize) / (maxCropSize - minCropSize)) * 100}%, #e5e7eb ${((cropSize - minCropSize) / (maxCropSize - minCropSize)) * 100}%, #e5e7eb 100%)`,
                // Enhanced mobile touch target
                minHeight: window.innerWidth < 768 ? '40px' : 'auto'
              }}
            />
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} min-w-[3rem] text-right font-mono`}>
            {Math.round(cropSize)}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex ${showCancelButton ? 'space-x-4' : 'justify-center'} sm:space-x-4 px-3 sm:px-4 w-full max-w-sm`}>
        {showCancelButton && (
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className={`flex-1 px-6 py-4 sm:py-3 rounded-lg font-medium transition-colors min-h-[48px] text-base sm:text-sm ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-500'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400'
            } active:scale-95 transition-transform`}
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleConfirm}
          disabled={isProcessing}
          className={`${showCancelButton ? 'flex-1' : 'min-w-[200px]'} px-6 py-4 sm:py-3 rounded-lg font-medium transition-all duration-200 min-h-[48px] text-base sm:text-sm ${
            isProcessing
              ? 'bg-purple-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 active:bg-purple-800 hover:shadow-lg'
          } text-white disabled:text-gray-300 active:scale-95 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50`}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Processing...
            </span>
          ) : (
            'Confirm'
          )}
        </button>
      </div>
    </div>
  );
};

export default ProfilePhotoCropper;